import PipelineStagesFlow from './PipelineStagesFlow';
import { usePipelineStagesDetails } from './usePipelineStagesDetails';
import { LoadingSpinner } from '../common';
import { PipelineStageStatus } from '../../types/pipelineStages';

interface PipelineStagesPanelProps {
  buildId: number;
  className?: string;
}

export function PipelineStagesPanel({ buildId, className = '' }: PipelineStagesPanelProps) {
  const {
    stagesData,
    isExpanded,
    isLoading,
    error,
    handleToggleExpanded,
    handleRefresh,
  } = usePipelineStagesDetails(buildId);

  return (
    <div className={`border border-gray-200 rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleToggleExpanded}
            className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            <svg
              className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            <span>Pipeline Stages</span>
          </button>
          
          {stagesData && (
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <span>•</span>
              <span>{stagesData.stages.length} stages</span>
              <span>•</span>
              <span className={`font-medium ${getOverallStatusColor(stagesData.overallStatus)}`}>
                {formatStatus(stagesData.overallStatus)}
              </span>
            </div>
          )}
        </div>

        {isExpanded && (
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            )}
            <span>Refresh</span>
          </button>
        )}
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4">
          {isLoading && !stagesData ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="sm" className="mr-2" />
              <span className="text-sm text-gray-600">Loading pipeline stages...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8 text-sm text-red-600">
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              Failed to load pipeline stages
            </div>
          ) : stagesData ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <strong>{stagesData.pipelineName}</strong> - Build #{stagesData.buildId}
              </div>
              
              <PipelineStagesFlow 
                pipelineStages={stagesData} 
                className="border-0" 
              />
            </div>
          ) : (
            <div className="flex items-center justify-center py-8 text-sm text-gray-500">
              No pipeline stages available
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getOverallStatusColor(status: PipelineStageStatus): string {
  switch (status) {
    case PipelineStageStatus.Succeeded:
      return 'text-green-600';
    case PipelineStageStatus.Failed:
      return 'text-red-600';
    case PipelineStageStatus.InProgress:
      return 'text-yellow-600';
    case PipelineStageStatus.Canceled:
      return 'text-gray-600';
    case PipelineStageStatus.Skipped:
      return 'text-blue-600';
    case PipelineStageStatus.PartiallySucceeded:
      return 'text-orange-600';
    case PipelineStageStatus.NotStarted:
      return 'text-gray-500';
    default:
      return 'text-gray-600';
  }
}

function formatStatus(status: PipelineStageStatus): string {
  // Add spaces before capital letters and format
  return status.replace(/([A-Z])/g, ' $1').trim();
}